/**
 * URL-Zustand des Verlaufs: Ansicht, Apartment (nur eines der Objektwahl; ein unsichtbares wird
 * entfernt), Filter mit Chips und die Texte der technischen Filter, die auch halb stehen bleiben.
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useObjectScope } from '@/shared/object-scope';
import { historyChips, type HistoryChip } from './historyChips';
import {
  CLEARED_FILTERS,
  applyDraft,
  draftOf,
  pendingId,
  type HistoryFilterDraft,
  type TechnicalTexts,
} from './historyFilterDraft';
import { historyQuery } from './historyQuery';
import { GUID, historyView, type DynamicPricingHistorySearch } from './historyUrlState';

const LOCAL_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useHistorySearch() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as DynamicPricingHistorySearch;
  const scope = useObjectScope();
  const { propertyIds, group, groups, groupId } = scope;
  // Ohne Objektliste ist ein Apartment nicht ungültig, sondern unbekannt – der Link bleibt stehen,
  // und ohne bekannte Sicht wird nichts abgefragt (sonst ginge es über den ganzen Workspace).
  const scopeMissing =
    !scope.isLoading && (scope.isError || scope.isOffline) && groups.length === 0;
  const scopeLoading = scope.isLoading || scopeMissing;
  const apartments = useMemo(
    () =>
      (groupId ? groups.filter((item) => item.id === groupId) : groups).flatMap(
        (item) => item.apartments,
      ),
    [groupId, groups],
  );
  const apartment = apartments.some((item) => item.id === search.apartment)
    ? search.apartment
    : undefined;
  const [texts, setTexts] = useState<TechnicalTexts>(() => ({
    configurationText: search.configurationVersion ?? '',
    profileText: search.profileVersion ?? '',
  }));
  const patch = (next: Partial<DynamicPricingHistorySearch>) =>
    void navigate({ to: '/dynamic-pricing/history', search: { ...search, ...next } as never });
  useEffect(() => {
    if (!scopeLoading && search.apartment && !apartment) {
      const { apartment: _, ...safe } = search;
      void navigate({ to: '/dynamic-pricing/history', search: safe as never, replace: true });
    }
  }, [apartment, navigate, scopeLoading, search]);
  // Zurück/Vor oder ein ✕ ändern die ID in der URL; eine halbe Eingabe ohne URL-Wert bleibt.
  useEffect(() => {
    setTexts((current) => ({
      configurationText: search.configurationVersion ?? keepHalf(current.configurationText),
      profileText: search.profileVersion ?? keepHalf(current.profileText),
    }));
  }, [search.configurationVersion, search.profileVersion]);

  const view = historyView(search.view);
  const chips = historyChips(search, LOCAL_ZONE, view);
  return {
    search,
    view,
    scope,
    scopeMissing,
    apartments,
    apartment,
    scopeLoading,
    query: historyQuery(search, { groupId: group?.id ?? null, propertyIds, apartment }),
    chips,
    /** Eine halbe technische ID steht im Feld, wirkt aber nicht – auch bei geschlossenem Panel sagen. */
    pendingTechnicalId: pendingId(texts.configurationText) || pendingId(texts.profileText),
    /** „Filter · n“ zählt nur, was in der gezeigten Ansicht wirkt. */
    activeFilters: chips.filter((chip) => !chip.onlyIn).length,
    patch,
    draft: () => draftOf(search, texts),
    apply: (draft: HistoryFilterDraft) => {
      setTexts({ configurationText: draft.configurationText, profileText: draft.profileText });
      patch(applyDraft(draft));
    },
    removeChip: (chip: HistoryChip) =>
      patch(Object.fromEntries(chip.clears.map((key) => [key, undefined]))),
    clearFilters: () => {
      setTexts({ configurationText: '', profileText: '' });
      patch(CLEARED_FILTERS);
    },
  };
}

/** Eine vollständige ID ohne URL-Wert wurde entfernt; eine halbe bleibt stehen. */
function keepHalf(text: string) {
  return GUID.test(text.trim()) ? '' : text;
}
